import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, CornerDownRight } from "lucide-react";
import { fmtTs, fmtSeconds } from "@/lib/format";

// Comments thread. mode: "studio" | "client"
export function Comments({ postId, isVideo, videoSecond, onSeek, mode = "studio", token, initialComments }) {
  const [comments, setComments] = useState(initialComments || []);
  const [text, setText] = useState("");
  const [scope, setScope] = useState("interno");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (mode === "studio") {
      api.get(`/posts/${postId}/comments`).then((r) => setComments(r.data));
    } else {
      setComments(initialComments || []);
    }
    /* eslint-disable-next-line */
  }, [postId]);

  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const payload = { text, scope: mode === "client" ? "cliente" : scope, video_second: isVideo ? videoSecond : null };
      if (mode === "client") {
        const { data } = await api.post(`/c/${token}/posts/${postId}/comments`, payload);
        setComments((c) => [...c, data]);
      } else {
        const { data } = await api.post(`/posts/${postId}/comments`, payload);
        setComments((c) => [...c, data]);
      }
      setText("");
    } catch (e) {
      toast.error("No he podido guardar el comentario.");
    } finally {
      setSending(false);
    }
  };

  const toggleResolve = async (id) => {
    const { data } = await api.patch(`/comments/${id}/resolve`);
    setComments((cs) => cs.map((c) => (c.id === id ? data : c)));
  };

  const active = comments.filter((c) => !c.resolved);
  const resolved = comments.filter((c) => c.resolved);

  return (
    <div className="flex flex-col h-full" data-testid="comments-panel">
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {active.length === 0 && resolved.length === 0 && (
          <p className="text-sm text-subtle py-8 text-center">Aún no hay comentarios. Empieza la conversación abajo.</p>
        )}
        {active.map((c) => (
          <CommentItem key={c.id} c={c} onSeek={onSeek} onResolve={mode === "studio" ? () => toggleResolve(c.id) : null} />
        ))}
        {resolved.length > 0 && (
          <details className="border-t border-line pt-2">
            <summary className="text-xs text-subtle cursor-pointer font-data">{resolved.length} resueltos</summary>
            <div className="space-y-3 mt-3 opacity-60">
              {resolved.map((c) => (
                <CommentItem key={c.id} c={c} onSeek={onSeek} onResolve={mode === "studio" ? () => toggleResolve(c.id) : null} />
              ))}
            </div>
          </details>
        )}
      </div>

      <div className="border-t border-line pt-3 mt-3 space-y-2">
        {mode === "studio" && (
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setScope(scope === "interno" ? "cliente" : "interno")}
              data-testid="comment-scope-toggle"
              className={`px-2 py-1 rounded-sm border font-data ${scope === "interno" ? "bg-app border-line text-ink" : "border-line text-subtle"}`}
            >
              {scope === "interno" ? "Nota interna" : "Visible para el cliente"}
            </button>
            <span className="text-subtle">{scope === "interno" ? "Solo el estudio lo verá" : "El cliente lo verá"}</span>
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            placeholder={isVideo ? `Comenta el segundo ${fmtSeconds(videoSecond)}…` : "Escribe un comentario…"}
            rows={2}
            data-testid="comment-input"
            className="flex-1 border border-line rounded-sm px-3 py-2 text-sm resize-none bg-surface focus:outline-none"
          />
        </div>
        <button
          onClick={submit}
          disabled={sending || !text.trim()}
          data-testid="comment-submit"
          className="w-full py-2 text-sm text-white rounded-sm disabled:opacity-40"
          style={{ background: "var(--brand-color)" }}
        >
          Comentar
        </button>
      </div>
    </div>
  );
}

// exposed for client to inject its visible comments
Comments.Item = CommentItem;

function CommentItem({ c, onSeek, onResolve }) {
  const internal = c.scope === "interno";
  return (
    <div className={`p-2.5 rounded-sm border ${internal ? "bg-app border-line" : "border-line bg-surface"}`} data-testid="comment-item">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium">{c.author}</span>
        {internal && <span className="text-[10px] font-data px-1.5 py-0.5 bg-ink text-white rounded-sm">interno</span>}
        {c.author_type === "cliente" && <span className="text-[10px] font-data px-1.5 py-0.5 border border-line rounded-sm text-subtle">cliente</span>}
        <span className="text-[11px] font-data text-subtle ml-auto">{fmtTs(c.created_at)}</span>
      </div>
      {c.video_second != null && onSeek && (
        <button onClick={() => onSeek(c.video_second)} data-testid="comment-seek" className="font-data text-xs mb-1 flex items-center gap-1" style={{ color: "var(--brand-color)" }}>
          <CornerDownRight size={12} /> {fmtSeconds(c.video_second)}
        </button>
      )}
      <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.text}</p>
      {onResolve && (
        <button onClick={onResolve} className="text-xs text-subtle mt-1.5 flex items-center gap-1 hover:text-ink" data-testid="comment-resolve">
          <Check size={12} /> {c.resolved ? "Reabrir" : "Marcar resuelto"}
        </button>
      )}
    </div>
  );
}
